/**
 * Small fuzzy matcher for the quick switcher and command palette. Every query
 * character must appear in order; consecutive runs, word starts and a prefix
 * match score higher, and shorter labels win ties. Returns null for no match.
 */
export function fuzzyScore(query: string, text: string): number | null {
  const q = query.trim().toLowerCase();
  if (!q) return 0;
  const t = text.toLowerCase();
  if (t === q) return 1000;
  if (t.startsWith(q)) return 800 - t.length;
  const idx = t.indexOf(q);
  if (idx > 0) return 600 - idx - t.length + (/[\s\-_#@/.]/.test(t[idx - 1]) ? 100 : 0);
  let score = 0;
  let ti = 0;
  let run = 0;
  for (const ch of q) {
    if (ch === " ") continue;
    const found = t.indexOf(ch, ti);
    if (found === -1) return null;
    run = found === ti ? run + 1 : 0;
    const wordStart = found === 0 || /[\s\-_#@/.]/.test(t[found - 1]);
    score += 10 + run * 5 + (wordStart ? 15 : 0) - Math.min(found - ti, 10);
    ti = found + 1;
  }
  return score - t.length * 0.5;
}

/** Filter and sort items by their best-matching field. */
export function fuzzyFilter<T>(items: T[], query: string, fields: (item: T) => (string | undefined | null)[]): T[] {
  if (!query.trim()) return items;
  const scored: { item: T; score: number }[] = [];
  for (const item of items) {
    let best: number | null = null;
    for (const f of fields(item)) {
      if (!f) continue;
      const s = fuzzyScore(query, f);
      if (s !== null && (best === null || s > best)) best = s;
    }
    if (best !== null) scored.push({ item, score: best });
  }
  return scored.sort((a, b) => b.score - a.score).map((s) => s.item);
}
